"use client";

import React, { useState, useMemo } from "react";
import { MapPin, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
const CATEGORIES = ["All", "North", "Center", "South", "Jewish"];
function SiteFilterGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [activeCategory, setActiveCategory] = useState("All");
  const filteredPosts = useMemo(() => {
    if (!posts) return [];
    if (activeCategory === "All") return posts;
    return posts.filter(post => {
      const postCategory = post.customFields?.category;
      return postCategory === activeCategory;
    });
  }, [posts, activeCategory]);
  if (loading) {
    return <div className="space-y-12">
        {}
        <div className="flex justify-center gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-24 rounded-full" />)}
        </div>
        {}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="space-y-4">
              <Skeleton className="aspect-[4/3] w-full rounded-lg" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
        <p className="text-muted-foreground mb-4">Unable to load sites at this time.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>;
  }
  return <div className="space-y-12">
      {}
      <div className="flex flex-wrap justify-center gap-3">
        {CATEGORIES.map((category, i) => <Button key={category} data-index={i} onClick={() => setActiveCategory(category)} variant={activeCategory === category ? "default" : "ghost"} className={cn("rounded-full px-6 transition-all duration-300", activeCategory === category ? "shadow-md" : "bg-secondary/50 text-muted-foreground hover:bg-secondary hover:text-foreground")}>
            {category}
          </Button>)}
      </div>

      {}
      {filteredPosts.length > 0 ? <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="group block h-full">
              <Card data-index={i} className="h-full overflow-hidden border-transparent bg-transparent shadow-none transition-all duration-300 hover:-translate-y-1">
                {}
                <div className="relative mb-5 overflow-hidden rounded-lg shadow-sm transition-shadow duration-300 group-hover:shadow-md">
                  <div className="aspect-[4/3] w-full bg-muted">
                    {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/30 text-muted-foreground">
                        <MapPin className="h-12 w-12 opacity-20" />
                      </div>}
                  </div>
                  
                  {}
                  {post.customFields?.category && <Badge variant="secondary" className="absolute top-4 left-4 bg-white/90 text-primary backdrop-blur-sm hover:bg-white">
                      {post.customFields.category}
                    </Badge>}
                </div>

                {}
                <CardContent className="px-1 py-0">
                  <h3 className="font-serif text-xl font-bold text-primary transition-colors group-hover:text-ring">
                    {post.title}
                  </h3>
                  {post.customFields?.related_sites && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                      Explore related experiences in this region.
                    </p>}
                </CardContent>

                {}
                <CardFooter className="mt-4 px-1 py-0">
                  <span className="flex items-center text-sm font-medium text-ring group-hover:underline">
                    View Details <ArrowRight className="ml-1 h-4 w-4" />
                  </span>
                </CardFooter>
              </Card>
            </Link>)}
        </div> : <div className="py-20 text-center">
          <p className="text-lg text-muted-foreground">
            No sites found in the "{activeCategory}" category.
          </p>
          <Button variant="link" onClick={() => setActiveCategory("All")} className="mt-2 text-primary">
            View all sites
          </Button>
        </div>}
    </div>;
}
export default function SiteFilterGrid() {
  const wp_query_params = {
    post_type: "site",
    per_page: 100,
    orderby: "title",
    order: "ASC",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="sitefiltergrid" className="bg-background py-24">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="font-serif text-4xl font-bold text-primary md:text-5xl">
            Explore Our Sites
          </h2>
          <div className="mx-auto mt-4 h-1 w-24 bg-ring/30"></div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <SiteFilterGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}